import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { ApiService } from '../services/api.service';
import { EncryptionService } from '../services/encryption.service';
import { DecryptionService } from '../services/decryption.service';
import { environment } from '../../environments/environment';
import { isPlatformBrowser } from '@angular/common';

@Injectable()
export class EncryptDecryptInterceptor implements HttpInterceptor {
  constructor(
    private apiService: ApiService,
    private encryptionService: EncryptionService,
    private decryptionService: DecryptionService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    let endpointsToEncrypt: { endpoint: string; method: string }[] = [];

    if (!environment.production) {
      endpointsToEncrypt = [
        ...endpointsToEncrypt,
        {
          endpoint: this.apiService.nssoProfileUrl,
          method: 'GET',
        },
        {
          endpoint: this.apiService.nssoProfileUrl,
          method: 'PUT',
        },
        {
          endpoint: this.apiService.nssoEntityUrl,
          method: 'PUT',
        },
        {
          endpoint: this.apiService.initAuthUrl,
          method: 'GET',
        },
        {
          endpoint: this.apiService.entityInitAuthUrl,
          method: 'GET',
        },
        {
          endpoint: this.apiService.openProfileDetailsUrl,
          method: 'POST',
        },
        {
          endpoint: this.apiService.organisationProfileUrl,
          method: 'POST',
        },
        {
          endpoint: this.apiService.organisationMemberUrl,
          method: 'POST',
        },
      ];
    }

    const isEndpointEnc = endpointsToEncrypt.some(
      ({ endpoint, method }) =>
        req.url.endsWith(endpoint) && req.method === method
    );

    const platformId = inject(PLATFORM_ID);

    if (isEndpointEnc && isPlatformBrowser(platformId)) {
      const reqBodyJSON = JSON.stringify(req.body || {});
      const { encryptedBody, key, iv } =
        this.encryptionService.encryptRequestBody(reqBodyJSON);

      return new Observable<string[]>((observer) => {
        Promise.all([
          this.encryptionService.encryptViaRSA(key),
          this.encryptionService.encryptViaRSA(iv),
          this.encryptionService.encryptViaRSA(new Date().getTime().toString()),
        ])
          .then((result) => {
            observer.next(result);
            observer.complete();
          })
          .catch((_) => {
            observer.error(
              new HttpErrorResponse({
                error: { message: 'Something went wrong' },
              })
            );
          });
      }).pipe(
        concatMap(([encryptedKey, encryptedIv, encryptedNounce]) => {
          const clonedReqBody = req.clone({
            headers: environment.production
              ? req.headers.set('key', encryptedKey).set('iv', encryptedIv)
              : req.headers
                  .set('X-REQUEST-KEY', encryptedKey)
                  .set('X-REQUEST-IV', encryptedIv)
                  .set('X-REQUEST-NOUNCE', encryptedNounce),
            body: req.body && encryptedBody,
            responseType: 'text',
          });

          return next.handle(clonedReqBody).pipe(
            map((event) => {
              if (event.type !== HttpEventType.Response) {
                return event;
              }

              let decryptedResp;

              try {
                decryptedResp = JSON.parse(
                  this.decryptionService.decryptRequestBody(event.body, key, iv)
                );
              } catch (error) {
                decryptedResp = JSON.parse(event.body);
              }

              return event.clone({ body: decryptedResp });
            })
          );
        })
      );
    }

    return next.handle(req);
  }
}
